import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import { ModalsKey } from '@/enums/modalsKey';
import Link from '@/components/core/Link';
import styles from './styles.module.scss';
import { Animation } from '@starsoft/common/components';
import { Button } from '@starsoft/common/components';
import { AnimatePresence } from 'framer-motion';
import { AuthLoginRequestDto } from '@/services/dto/auth/auth-login-request.dto';
import { memo, PropsWithChildren } from 'react';
import { AuthService } from '@/services/AuthService';
import Requires2faContent from './Requires2fa';
import useAuthForm from '@/hooks/auth/useAuthForm';
import { SettingLayoutComponentId } from '@starsoft/common/models';
import AuthModalContentLoginLayoutOpt from './Opt';
import { useAuthFormState } from '@/hooks/auth/useAuthFormState';
import { useAuthFormButtonLabel } from '@/hooks/auth/useAuthFormButtonLabel';
import { CookiesKey, KycCheckpointId } from '@starsoft/common/enums';
import { useSystemConfiguration } from '@/hooks/theme/useSystemConfiguration';
import { OriginId } from '@starsoft/common/models/origin/id';
import { getCookie } from 'cookies-next';
import PassKeyButton from '../../Common/PassKeyButton';
import { useUserHasPassKey } from '@/api/passkeys';
import { Collapse } from '@mui/material';
function LoginLayout({
  children,
  id = 1n,
}: PropsWithChildren<{ id?: bigint }>) {
  const methods = useForm<AuthLoginRequestDto>();
  const { handleSubmit } = methods;
  const { t } = useTranslation('login');
  const {
    isFormStep,
    step,
    email,
    kycCheckpointId,
    useLoginMutation: { isLoading, requires2fa, onClose },
    onSubmit,
    onError,
  } = useAuthForm({ methods });

  const { userHasPassKey } = useUserHasPassKey({
    email,
  });
  const formState = useAuthFormState({ methods });
  const label = useAuthFormButtonLabel({ methods });
  const isVariant =
    id?.toString() === SettingLayoutComponentId.LoginV3?.toString();
  const { originId } = useSystemConfiguration();

  return (
    <Animation type="fadeInLeft" key={step} triggerOnce>
      <FormProvider {...methods}>
        <div
          className={`${styles.container} ${isVariant ? styles['container--variant'] : ''} `}
        >
          <form
            className={styles.container__form}
            id="login-form"
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <AnimatePresence mode="wait">
              {requires2fa && (
                <Requires2faContent onClose={onClose} isLoading={isLoading} />
              )}
            </AnimatePresence>
            {children}
            {isFormStep && (
              <Link
                className={`${styles.container__form__link} ${isVariant ? styles['container__form__link--variant'] : ''} `}
                modal={ModalsKey.RecoverPassword}
              >
                {t('forgot_password')}
              </Link>
            )}
            {kycCheckpointId?.toString() !==
              KycCheckpointId.DocumentSubmission?.toString() && (
              <div className={styles.container__button}>
                <Button
                  type="submit"
                  form="login-form"
                  isloading={
                    isLoading ||
                    (AuthService.isAuthorized() &&
                      getCookie(CookiesKey.HasKyc) === 'true')
                  }
                  disabled={isLoading || formState}
                >
                  {label}
                </Button>
              </div>
            )}
          </form>
          <Collapse in={userHasPassKey} sx={{ width: '100%' }}>
            <PassKeyButton />
          </Collapse>
          <AuthModalContentLoginLayoutOpt
            isVariant={isVariant}
            showOauth={
              isFormStep && originId?.toString() !== OriginId.Brazil?.toString()
            }
          />
        </div>
      </FormProvider>
    </Animation>
  );
}

export default memo(LoginLayout);
